// src/store/cities.store.ts
import { makeAutoObservable, runInAction } from 'mobx';
import apiService from 'api';
import type { ICity } from 'api/cities';

type Id = number | string;

type RemoveOptions = {
  force?: boolean;
  signal?: AbortSignal;
};

class CitiesStore {
  list: ICity[] = [];

  current: ICity | null = null;

  loading = false;

  /** id'ы, которые сейчас удаляются — удобно для дизейбла кнопки/лоадера */
  removingIds = new Set<number>();

  /** последнее сообщение об ошибке удаления/загрузки */
  lastError: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  resetCurrent() {
    this.current = null;
  }

  setLastError(msg: string | null) {
    this.lastError = msg;
  }

  getById(id: Id) {
    const nid = Number(id);
    return this.list.find((c) => c.id === nid) || null;
  }

  async loadList() {
    this.loading = true;
    this.lastError = null;
    try {
      const { data } = await apiService.cities.getList();
      runInAction(() => {
        this.list = data;
      });
    } catch (e: any) {
      runInAction(() => {
        this.lastError =
          e?.response?.data?.message ||
          e?.message ||
          'Не удалось загрузить города';
      });
      throw e;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async loadOne(id: Id) {
    this.loading = true;
    this.lastError = null;
    try {
      const { data } = await apiService.cities.getOne(id);
      runInAction(() => {
        this.current = data;
      });
    } catch (e: any) {
      runInAction(() => {
        this.lastError =
          e?.response?.data?.message ||
          e?.message ||
          'Не удалось загрузить город';
      });
      throw e;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async create(payload: Partial<ICity>) {
    const { data } = await apiService.cities.create(payload);
    runInAction(() => {
      this.list.unshift(data);
      this.current = data;
    });
    return data;
  }

  async update(id: Id, payload: Partial<ICity>) {
    const { data } = await apiService.cities.update(id, payload);
    runInAction(() => {
      this.list = this.list.map((c) => (c.id === data.id ? data : c));
      this.current = data;
    });
    return data;
  }

  /**
   * Удаление одного города с учётом наличия безопасных методов в apiService
   */
  async remove(id: Id, opts?: RemoveOptions) {
    const nid = Number(id);
    this.lastError = null;
    this.removingIds.add(nid);

    try {
      // если в API есть removeSafe — используем его
      if (
        'removeSafe' in apiService.cities &&
        typeof apiService.cities.removeSafe === 'function'
      ) {
        const res = await (apiService.cities as any).removeSafe(id, opts);
        if (!res?.ok) {
          const msg = res?.message || 'Не удалось удалить город';
          // eslint-disable-next-line no-return-assign
          runInAction(() => (this.lastError = msg));
          throw new Error(msg);
        }
      } else {
        // обычный DELETE
        await apiService.cities.remove(id);
      }

      runInAction(() => {
        this.list = this.list.filter((c) => c.id !== nid);
        if (this.current?.id === nid) this.current = null;
      });
    } catch (e: any) {
      // сообщение уже положили в lastError, если это removeSafe
      if (!this.lastError) {
        runInAction(() => {
          this.lastError =
            e?.response?.data?.message ||
            e?.message ||
            'Не удалось удалить город';
        });
      }
      throw e;
    } finally {
      runInAction(() => {
        this.removingIds.delete(nid);
      });
    }
  }

  /**
   * Пакетное удаление; возвращает {ok, failed} для показа итогов
   */
  async removeMany(
    ids: Id[],
    opts?: RemoveOptions
  ): Promise<{
    ok: number[];
    failed: Array<{ id: number; message: string; status?: number }>;
  }> {
    this.lastError = null;
    const numIds = ids.map((i) => Number(i));
    numIds.forEach((i) => this.removingIds.add(i));

    try {
      if (
        'removeMany' in apiService.cities &&
        typeof (apiService.cities as any).removeMany === 'function'
      ) {
        const res = await (apiService.cities as any).removeMany(ids, opts);
        runInAction(() => {
          if (res.ok?.length) {
            this.list = this.list.filter((c) => !res.ok.includes(c.id));
            if (this.current && res.ok.includes(this.current.id))
              this.current = null;
          }
        });
        if (res.failed?.length) {
          // можно агрегировать в lastError краткое резюме
          const first = res.failed[0];
          runInAction(() => {
            this.lastError =
              res.failed.length === 1
                ? first.message
                : `Не удалось удалить ${res.failed.length} из ${ids.length}`;
          });
        }
        return {
          ok: res.ok?.map(Number) || [],
          failed:
            res.failed?.map((f: any) => ({
              id: Number(f.id),
              message: f.message,
              status: f.status
            })) || []
        };
      }

      // Фоллбэк: нет removeMany — делаем сами
      const results = await Promise.allSettled(
        ids.map((id) =>
          apiService.cities
            .remove(id)
            .then(() => ({ ok: true as const, id }))
            .catch((e: any) => ({
              ok: false as const,
              id,
              message:
                e?.response?.data?.message ||
                e?.message ||
                'Не удалось удалить',
              status: e?.response?.status
            }))
        )
      );

      const ok: number[] = [];
      const failed: Array<{ id: number; message: string; status?: number }> =
        [];

      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          ok.push(Number(r.value.id));
        } else {
          const v = r.reason;
          failed.push({
            id: Number(v?.id),
            message: v?.message || 'Не удалось удалить',
            status: v?.status
          });
        }
      });

      runInAction(() => {
        if (ok.length) {
          this.list = this.list.filter((c) => !ok.includes(c.id));
          if (this.current && ok.includes(this.current.id)) this.current = null;
        }
        if (failed.length) {
          this.lastError =
            failed.length === 1
              ? failed[0].message
              : `Не удалось удалить ${failed.length} из ${ids.length}`;
        }
      });

      return { ok, failed };
    } finally {
      runInAction(() => {
        numIds.forEach((i) => this.removingIds.delete(i));
      });
    }
  }
}

const cityStore = new CitiesStore();
export default cityStore;
